import {
  ApostleLite,
  ApostleSortBy,
  ApostleStatus,
  AttachApostle,
  NO_REPLACE_INDEX
} from '@apeiron/apostle'
import { PlanetMiddleLite } from '@apeiron/planet'
import { formatNumberString, MayNull, NextImage } from '@apeiron/temp'
import { Box, Button, styled } from '@mui/material'
import RemoveImage from '@public/icons/icon-remove.png'
import BGSelectCardJPG from '@public/images/image-select-card.jpg'
import MarketplaceApostleCard from '@src/components/apostles/card/MarketplaceApostleCard'
import ApostleAttachCard from '@src/components/apostles/manage/attach/ApostleAttachCard'
import { RenderPopupCardProps } from '@src/components/apostles/share/multi-select/ApostleMultiSelectionSlots'
import ApostleSelectionPopup from '@src/components/apostles/share/multi-select/ApostleSelectionPopup'
import PlanetSelectCard from '@src/components/planet/share/planetCard/PlanetSelectCard'
import { useApostleAttachContext } from '@src/contexts/apostle/ApostleAttachContext'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  display: flex;
  position: relative;
  ${props => props.theme.breakpoints.up('res768')} {
    max-width: 1000px;
    gap: 20px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    align-items: center;
    flex-direction: column;
    width: 100%;
  }
`

const ApostleRow = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  ${props => props.theme.breakpoints.up('res768')} {
    background: linear-gradient(90deg, #1e1e1e 0%, #000000 80%);
    gap: 21px;
    padding: 22px;
    width: 100%;
  }
  ${props => props.theme.breakpoints.between('res480', 'res768')} {
    background: linear-gradient(135deg, #1e1e1e 0%, #000000 80%);
    gap: 50px;
    justify-content: space-evenly;
    max-width: 500px;
    padding: 40px 30px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    background: linear-gradient(135deg, #1e1e1e 0%, #000000 80%);
    gap: 50px 20px;
    justify-content: space-evenly;
    max-width: 500px;
    padding: 40px 30px;
  }
`

const StyledApostleAttachCard = styled(ApostleAttachCard)`
  box-sizing: border-box;
  ${props => props.theme.breakpoints.up('res768')} {
    width: 130px;
    min-height: 245px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res768')} {
    width: 150px;
    min-height: 282px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 110px;
    min-height: 207px;
  }
`

const PlanetCard = styled(PlanetSelectCard)`
  height: 100%;
  ${props => props.theme.breakpoints.up('res768')} {
    min-width: 200px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res768')} {
    width: 240px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 200px;
  }
`

const RemoveButton = styled(Button)`
  position: absolute;

  ${props => props.theme.breakpoints.up('res768')} {
    right: -30px;
    top: -24px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res768')} {
    top: -10px;
    left: calc(50% + 100px);
    transform: translateX(-50%);
  }
  ${props => props.theme.breakpoints.down('res480')} {
    top: -10px;
    left: calc(50% + 80px);
    transform: translateX(-50%);
  }
`

const RemoveIcon = styled(NextImage)`
  aspect-ratio: 1;
  ${props => props.theme.breakpoints.up('res768')} {
    width: 40px;
  }
  ${props => props.theme.breakpoints.between('res480', 'res768')} {
    width: 50px;
  }
  ${props => props.theme.breakpoints.down('res768')} {
    width: 50px;
  }
`

const ApostleAttachRow: FC<Props> = (props: Props) => {
  const { className, key, planet, onPlanetClick, onPlanetRemove } = props

  const {
    displayAttachTable,
    originalAttachTable,
    configApostleSlotTable,
    addApostles,
    countAllowedSelection,
    removeApostle,
    replaceApostle,
    toggleApostleAsMaterial
  } = useApostleAttachContext()

  const [openSelectApostlePopup, setOpenSelectApostlePopup] =
    useState<boolean>(false)

  const { t } = useTranslation()

  const [replaceIndex, setReplaceIndex] = useState<number>(NO_REPLACE_INDEX)

  const [selectLimit, setSelectLimit] = useState<number>(1)

  // ? don't have re-select multiple items on apostle attachment
  const selectedApostles = useMemo(() => {
    return []
  }, [])

  const [selectingApostles, setSelectingApostles] = useState<ApostleLite[]>([])

  const preservedApostles = useMemo((): ApostleLite[] => {
    const extract = R.pipe<
      any,
      MayNull<ApostleLite>[][],
      MayNull<ApostleLite>[],
      ApostleLite[]
    >(R.values, R.flatten, R.reject(R.isNil))

    return extract(displayAttachTable)
  }, [displayAttachTable])

  const handleOnUnselectApostle = useCallback(
    (apostle: MayNull<ApostleLite>) => {
      if (R.isNotNil(apostle)) {
        setSelectingApostles(
          R.reject(
            (selectingApostle: ApostleLite) =>
              selectingApostle.id === apostle.id,
            selectingApostles
          )
        )
      }
    },
    [selectingApostles]
  )

  const handleOnRemovePlanet = useCallback(() => {
    if (R.isNotNil(planet) && onPlanetRemove) {
      onPlanetRemove(planet.planetID)
    }
  }, [onPlanetRemove, planet])

  const handleOnRemoveApostle = useCallback(
    (index: number) => {
      if (R.isNotNil(planet) && onPlanetRemove) {
        const apostle = displayAttachTable[planet.planetID]?.[index]

        if (R.isNotNil(apostle)) {
          handleOnUnselectApostle(apostle)
          removeApostle(planet.planetID, index)
        }
      }
    },
    [
      displayAttachTable,
      planet,
      handleOnUnselectApostle,
      removeApostle,
      onPlanetRemove
    ]
  )

  const handleOnApostleSelectClose = useCallback(() => {
    setOpenSelectApostlePopup(false)
    setReplaceIndex(NO_REPLACE_INDEX)
  }, [])

  const handleOnApostleSelectConfirm = useCallback(
    (apostles: MayNull<ApostleLite[]>) => {
      if (R.isNotNil(planet)) {
        if (replaceIndex === NO_REPLACE_INDEX) {
          // add apostles
          const confirmedApostles = R.map(apostle => {
            return R.assoc('materialize', false, apostle)
          }, apostles || [])

          addApostles(planet.planetID, confirmedApostles)
        } else {
          // replace apostle
          const confirmedApostle = R.head(apostles || [])

          if (R.isNotNil(confirmedApostle)) {
            replaceApostle(planet.planetID, confirmedApostle, replaceIndex)
          }
        }

        handleOnApostleSelectClose()
      }
    },
    [
      planet,
      replaceIndex,
      handleOnApostleSelectClose,
      addApostles,
      replaceApostle
    ]
  )

  const handleOnPopupPreselection = useCallback(() => {
    if (R.isNotNil(planet)) {
      const displayApostles = R.reject(
        R.isNil,
        displayAttachTable[planet.planetID] || []
      )

      const originalApostles = R.reject(
        R.isNil,
        originalAttachTable[planet.planetID] || []
      )

      const comparator = (a: ApostleLite, b: ApostleLite) => a.id === b.id

      const apostles = R.differenceWith(
        comparator,
        displayApostles,
        originalApostles
      )

      setSelectingApostles(apostles)
    }
  }, [displayAttachTable, originalAttachTable, planet])

  const handleOnAddApostle = useCallback(() => {
    if (R.isNotNil(planet)) {
      handleOnPopupPreselection()
      setOpenSelectApostlePopup(true)
      setSelectLimit(countAllowedSelection(planet.planetID))
    }
  }, [planet, handleOnPopupPreselection, countAllowedSelection])

  const handleOnChangeApostle = useCallback(
    (index: number) => {
      if (R.isNotNil(planet)) {
        handleOnPopupPreselection()
        setOpenSelectApostlePopup(true)
        setReplaceIndex(index)
        setSelectLimit(1)
      }
    },
    [planet, handleOnPopupPreselection]
  )

  const handleOnChangeApostleCheckbox = useCallback(
    (index: number) => {
      if (R.isNotNil(planet)) {
        toggleApostleAsMaterial(planet.planetID, index)
      }
    },
    [planet, toggleApostleAsMaterial]
  )

  const handleOnRenderApostleCard = useCallback(
    (params: RenderPopupCardProps) => {
      const { apostle, isSelected, onClick } = params

      const isPreserved =
        R.findIndex(
          preservedApostle => preservedApostle.id === apostle.id,
          preservedApostles
        ) !== -1

      const isStaked = apostle.status === ApostleStatus.Staked

      const renderStatus = () => {
        if (isSelected || isPreserved) {
          return t('common.selected')
        }

        if (isStaked) {
          return t('apeiron.common.staked')
        }

        return ''
      }

      return (
        <MarketplaceApostleCard
          apostle={apostle}
          disabled={isPreserved || isStaked}
          onClick={onClick}
          statusText={renderStatus()}
          redStatus={isPreserved || isStaked}
        />
      )
    },
    [preservedApostles, t]
  )

  const apostles = useMemo(() => {
    return (
      displayAttachTable[planet?.planetID || ''] ||
      R.repeat(null, configApostleSlotTable.length)
    )
  }, [configApostleSlotTable.length, displayAttachTable, planet?.planetID])

  const lockedCards = useMemo(() => {
    return Array(configApostleSlotTable.length - apostles.length)
      .fill(0)
      .map((_, index) => {
        const key = `locked${index}`

        return (
          <StyledApostleAttachCard
            key={key}
            planetId={planet?.planetID || ''}
            lockedAge={formatNumberString(
              configApostleSlotTable[index + apostles.length].age.toString()
            )}
          />
        )
      })
  }, [apostles, configApostleSlotTable, planet?.planetID])

  return (
    <MainContainer className={className} key={key}>
      <PlanetCard
        customBackgroundImage={BGSelectCardJPG}
        planet={planet || null}
        onAdd={onPlanetClick}
        onChange={onPlanetClick}
      />
      {planet && (
        <RemoveButton
          className='Custom-Remove-Button'
          onClick={handleOnRemovePlanet}
        >
          <RemoveIcon className='Custom-Remove-Icon' src={RemoveImage} />
        </RemoveButton>
      )}
      <ApostleRow>
        {RA.mapIndexed((apostle: MayNull<AttachApostle>, index: number) => {
          return (
            <StyledApostleAttachCard
              apostle={apostle}
              key={`apostle-card-${index}`}
              planetId={planet?.planetID || ''}
              onAddClick={handleOnAddApostle}
              onChangeClick={() => handleOnChangeApostle(index)}
              onRemoveClick={() => handleOnRemoveApostle(index)}
              onCheckboxChange={() => handleOnChangeApostleCheckbox(index)}
            />
          )
        }, apostles)}
        {lockedCards}
      </ApostleRow>
      {openSelectApostlePopup && (
        <ApostleSelectionPopup
          isOpen={openSelectApostlePopup}
          selectedApostles={selectedApostles}
          selectLimit={selectLimit}
          onClose={handleOnApostleSelectClose}
          onConfirm={handleOnApostleSelectConfirm}
          renderCard={handleOnRenderApostleCard}
          defaultSortInput={{ sortBy: ApostleSortBy.ApostleIVDesc }}
        />
      )}
    </MainContainer>
  )
}

type Props = {
  className?: string
  key?: string
  planet?: PlanetMiddleLite
  onPlanetClick: () => void
  onPlanetRemove?: (planetId: string) => void
}

export default ApostleAttachRow
